// The host's meeting verdict for one connect: first meeting, reconnect or re-meet,
// plus the meeting key both phones file it under. Runs on the host only (the phone
// whose code was found), right after it finds the existing connection and BEFORE any
// merge, banner or row write — the scanner adopts the answer from the host's reply.
//
// Latency (the stranger test): a first connect pays nothing; a different night decides
// at once. Only a same-night repeat whose meeting has a stored position waits — up to
// SAME_NIGHT_COORDS_WAIT_MS — for the host's GPS fix (coordinates only, no place
// lookup); no fix in time → the time alone decides.
//
// Permanent data layer: no React — carries to the native app.

import { getConnectionMetadata } from '../storage/userDataStorage';
import { getMeetingsForConnection } from './meetingRows';
import { hasVenueOrAddress, formatLocationForDisplay } from '../location';
import {
  MEETING_KIND,
  currentMeetingRow,
  decideMeeting,
  meetingFromMetadata,
  meetingFromRow,
  mintMeetingKey,
} from './meetingRule';

export const SAME_NIGHT_COORDS_WAIT_MS = 1500;

const within = (promise, ms) =>
  Promise.race([
    Promise.resolve(promise).catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve(null), ms)),
  ]);

/**
 * @param {Object} p
 * @param {string|null} p.existingChatRoomId - the pair's existing room, or null (never met)
 * @param {string} p.profileUserId - the host's own profile id (their meeting rows)
 * @param {Promise|null} p.coordsPromise - resolves to the host's GPS coords, or null
 * @param {string} p.nowIso - the connect instant
 * @param {number|null} p.nowUtcOffset - the host's offset now (a new meeting's offset)
 * @returns {Promise<{kind, key, existing, metAt, metUtcOffset}>} — for a reconnect, the
 *   key/start of the meeting being continued (key may be null: a meeting from before
 *   keys is continued as is); otherwise a freshly minted key starting now.
 */
export const decideHostMeeting = async ({
  existingChatRoomId,
  profileUserId,
  coordsPromise,
  nowIso,
  nowUtcOffset,
}) => {
  const fresh = (kind, existing = null) => ({
    kind,
    key: mintMeetingKey(),
    existing,
    metAt: nowIso,
    metUtcOffset: nowUtcOffset,
  });
  if (!existingChatRoomId) return fresh(MEETING_KIND.FIRST);

  // The meeting the host's own record describes; no row (a failed seed, or a pair
  // first met through the guest flow) → the record itself is the meeting. The two
  // reads are independent — one round trip on the handshake, not two.
  const [metadata, rows] = await Promise.all([
    getConnectionMetadata(existingChatRoomId),
    getMeetingsForConnection(existingChatRoomId, profileUserId),
  ]);
  const row = currentMeetingRow(rows, metadata?.meetingKey);
  const existing = meetingFromRow(row) || meetingFromMetadata(metadata);

  // The night decides first, with no wait
  let kind = decideMeeting({ existing, nowIso, hostCoords: null });
  if (kind === MEETING_KIND.RECONNECT && existing?.coords) {
    const hostCoords = await within(coordsPromise, SAME_NIGHT_COORDS_WAIT_MS);
    kind = decideMeeting({ existing, nowIso, hostCoords });
  }

  if (kind === MEETING_KIND.RECONNECT) {
    return {
      kind,
      key: existing.key,
      existing,
      metAt: existing.metAt,
      metUtcOffset: existing.metUtcOffset,
    };
  }
  // A pair with history but no readable meeting at all is a re-meet, not a first
  return fresh(MEETING_KIND.REMEET, existing);
};

/**
 * This phone's record for a new meeting — from the host's verdict (or, on the scanner,
 * the verdict the reply carried): its key, start and offset, how they connected, and
 * this phone's own place (a snapshot; a late fix fills in after).
 *
 * @param {{key, metAt, metUtcOffset, via?}} meeting
 * @param {Object} p
 * @param {string} p.exchangeType - 'pxp' | 'anon_profile'
 * @param {Object|null} p.locationData - this phone's location snapshot
 * @param {string} p.nowIso
 */
export const meetingRecordFrom = (meeting, { exchangeType, locationData, nowIso }) => {
  const record = {
    meetingDate: meeting.metAt,
    meetingUtcOffset: meeting.metUtcOffset,
    ...(meeting.key ? { meetingKey: meeting.key } : {}),
    via: meeting.via || 'scan',
    lastUpdated: nowIso,
    exchangeType,
  };
  if (locationData) {
    record.locationData = locationData;
    if (hasVenueOrAddress(locationData)) record.location = formatLocationForDisplay(locationData);
  }
  return record;
};
